/**
 * Track H — toast notifications.
 *
 * Fixed bottom-right stack, newest at the bottom. Kinds carry a neutral inline
 * SVG icon and a colored accent edge. Auto-dismiss after 5s (errors 10s);
 * hovering the stack pauses every countdown; the X button dismisses on demand.
 * At most 5 toasts show at once — older ones collapse behind a "+N more" chip.
 *
 * Styling is self-contained (injected <style>, `mopa-toast-` prefix) and reads
 * track G's design tokens via var(--token, fallback) when present. Message text
 * is always set via textContent so file-derived warnings can never inject HTML.
 */

export type ToastKind = "info" | "warn" | "error" | "success";

interface ToastEntry {
  el: HTMLElement;
  remaining: number;
  startAt: number;
  timer: number;
  dismissing: boolean;
  removed: boolean;
}

const TOAST_STYLE_ID = "mopa-toast-style";
const MAX_VISIBLE = 5;
const DEFAULT_MS = 5000;
const ERROR_MS = 10000;

const ICON: Record<ToastKind, string> = {
  info: '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm.9 12H7.1V7h1.8v5zM8 5.6A1.05 1.05 0 118 3.5a1.05 1.05 0 010 2.1z"/>',
  success:
    '<path d="M8 0a8 8 0 100 16A8 8 0 008 0zm3.7 5.7l-4.2 4.6a.9.9 0 01-1.31.03L3.9 8.42a.9.9 0 011.28-1.27l1.66 1.66 3.55-3.9A.9.9 0 0111.7 5.7z"/>',
  warn: '<path fill-rule="evenodd" d="M7.13 1.6a1 1 0 011.74 0l6 10.5A1 1 0 0114 13.6H2a1 1 0 01-.87-1.5l6-10.5zM8 5.2a.9.9 0 00-.9.98l.3 3.3a.6.6 0 001.2 0l.3-3.3A.9.9 0 008 5.2zm0 5.4a1 1 0 100 2 1 1 0 000-2z"/>',
  error:
    '<path fill-rule="evenodd" d="M8 0a8 8 0 100 16A8 8 0 008 0zM5.7 5.7a.9.9 0 011.27 0L8 6.73 9.03 5.7a.9.9 0 011.27 1.27L9.27 8l1.03 1.03a.9.9 0 01-1.27 1.27L8 9.27 6.97 10.3A.9.9 0 015.7 9.03L6.73 8 5.7 6.97a.9.9 0 010-1.27z"/>',
};

const X_ICON =
  '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M1.7 1.7a.8.8 0 011.13 0L6 4.87 9.17 1.7A.8.8 0 0110.3 2.83L7.13 6l3.17 3.17a.8.8 0 01-1.13 1.13L6 7.13 2.83 10.3A.8.8 0 011.7 9.17L4.87 6 1.7 2.83a.8.8 0 010-1.13z"/></svg>';

let hostEl: HTMLElement | null = null;
let stack: HTMLElement | null = null;
let moreChip: HTMLElement | null = null;
const entries: ToastEntry[] = [];
let paused = false;

function injectToastStyle(): void {
  if (typeof document === "undefined" || document.getElementById(TOAST_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = TOAST_STYLE_ID;
  el.textContent = `
.mopa-toast-stack {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 95;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 320px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}
.mopa-toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px;
  border-radius: var(--radius-sm, 6px);
  border: 1px solid var(--border, rgba(255,255,255,0.09));
  border-left: 3px solid var(--accent, #6ea8ff);
  background: var(--surface-3, #2a2a2e);
  color: var(--text-1, rgba(255,255,255,0.87));
  box-shadow: var(--shadow-lg, 0 18px 48px rgba(0,0,0,0.55));
  font-family: var(--font, system-ui, sans-serif);
  font-size: 13px;
  line-height: 1.4;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.mopa-toast-in { opacity: 1; transform: none; }
.mopa-toast-out { opacity: 0; transform: translateY(8px); }
.mopa-toast-icon { width: 16px; height: 16px; flex: none; margin-top: 1px; fill: var(--accent, #6ea8ff); }
.mopa-toast-info { border-left-color: var(--accent, #6ea8ff); }
.mopa-toast-info .mopa-toast-icon { fill: var(--accent, #6ea8ff); }
.mopa-toast-warn { border-left-color: var(--warn, #ffb454); }
.mopa-toast-warn .mopa-toast-icon { fill: var(--warn, #ffb454); }
.mopa-toast-error { border-left-color: var(--danger, #ff6b6b); }
.mopa-toast-error .mopa-toast-icon { fill: var(--danger, #ff6b6b); }
.mopa-toast-success { border-left-color: var(--success, #57d38c); }
.mopa-toast-success .mopa-toast-icon { fill: var(--success, #57d38c); }
.mopa-toast-msg { flex: 1; min-width: 0; overflow-wrap: anywhere; }
.mopa-toast-x {
  flex: none;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--text-3, rgba(255,255,255,0.38));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}
.mopa-toast-x:hover { color: var(--text-1, rgba(255,255,255,0.87)); background: var(--surface-4, #34343a); }
.mopa-toast-x svg { width: 12px; height: 12px; fill: currentColor; }
.mopa-toast-more {
  pointer-events: auto;
  align-self: center;
  font-size: 11.5px;
  color: var(--text-3, rgba(255,255,255,0.54));
  background: var(--surface-2, #212124);
  border: 1px solid var(--border, rgba(255,255,255,0.12));
  border-radius: 20px;
  padding: 2px 10px;
  display: none;
}
@media (prefers-reduced-motion: reduce) {
  .mopa-toast {
    transition: none;
    transform: none;
  }
  .mopa-toast-out { transform: none; }
}
`;
  document.head.appendChild(el);
}

function ensureStack(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  injectToastStyle();
  if (stack && stack.isConnected) return stack;
  const parent = hostEl ?? document.body;
  if (!parent) return null;
  const el = document.createElement("div");
  el.className = "mopa-toast-stack";
  el.setAttribute("aria-live", "polite");
  el.addEventListener("mouseenter", pauseAll);
  el.addEventListener("mouseleave", resumeAll);
  const chip = document.createElement("div");
  chip.className = "mopa-toast-more";
  el.appendChild(chip);
  parent.appendChild(el);
  stack = el;
  moreChip = chip;
  return el;
}

export function initToasts(host: HTMLElement): void {
  hostEl = host;
  if (stack) {
    stack.remove();
    stack = null;
    moreChip = null;
  }
  entries.length = 0;
  paused = false;
  ensureStack();
}

export function toast(kind: ToastKind, message: string, opts?: { timeoutMs?: number }): void {
  const st = ensureStack();
  if (!st) return;

  const timeoutMs = opts && typeof opts.timeoutMs === "number" ? opts.timeoutMs : kind === "error" ? ERROR_MS : DEFAULT_MS;

  const el = document.createElement("div");
  el.className = `mopa-toast mopa-toast-${kind}`;
  el.setAttribute("role", kind === "error" || kind === "warn" ? "alert" : "status");

  const icon = document.createElement("span");
  icon.className = "mopa-toast-icon-wrap";
  icon.innerHTML = `<svg class="mopa-toast-icon" viewBox="0 0 16 16" aria-hidden="true">${ICON[kind]}</svg>`;

  const msg = document.createElement("div");
  msg.className = "mopa-toast-msg";
  msg.textContent = message;

  const close = document.createElement("button");
  close.type = "button";
  close.className = "mopa-toast-x";
  close.setAttribute("aria-label", "Dismiss notification");
  close.innerHTML = X_ICON;

  el.append(icon.firstElementChild ?? icon, msg, close);
  st.appendChild(el);

  const entry: ToastEntry = {
    el,
    remaining: Math.max(0, timeoutMs),
    startAt: 0,
    timer: 0,
    dismissing: false,
    removed: false,
  };
  entries.push(entry);

  close.addEventListener("click", () => dismiss(entry));

  reflow();

  // Enter animation on the next frame so the transition runs.
  const raf = typeof requestAnimationFrame !== "undefined" ? requestAnimationFrame : (cb: () => void) => setTimeout(cb, 16);
  raf(() => el.classList.add("mopa-toast-in"));

  scheduleDismiss(entry);
}

function scheduleDismiss(entry: ToastEntry): void {
  if (paused || entry.dismissing || entry.remaining <= 0) return;
  if (typeof setTimeout === "undefined") return;
  entry.startAt = Date.now();
  entry.timer = setTimeout(() => {
    entry.timer = 0;
    dismiss(entry);
  }, entry.remaining) as unknown as number;
}

function dismiss(entry: ToastEntry): void {
  if (entry.dismissing) return;
  entry.dismissing = true;
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = 0;
  }
  entry.el.classList.remove("mopa-toast-in");
  entry.el.classList.add("mopa-toast-out");

  const finish = (): void => {
    if (entry.removed) return;
    entry.removed = true;
    entry.el.remove();
    const i = entries.indexOf(entry);
    if (i >= 0) entries.splice(i, 1);
    reflow();
  };
  entry.el.addEventListener("transitionend", finish, { once: true });
  if (typeof setTimeout !== "undefined") setTimeout(finish, 400);
}

function reflow(): void {
  const hidden = Math.max(0, entries.length - MAX_VISIBLE);
  for (let i = 0; i < entries.length; i++) {
    entries[i].el.style.display = i < hidden ? "none" : "";
  }
  if (moreChip) {
    if (hidden > 0) {
      moreChip.textContent = `+${hidden} more`;
      moreChip.style.display = "";
    } else {
      moreChip.style.display = "none";
    }
  }
}

function pauseAll(): void {
  if (paused) return;
  paused = true;
  const now = Date.now();
  for (const e of entries) {
    if (e.timer && !e.dismissing) {
      clearTimeout(e.timer);
      e.timer = 0;
      e.remaining = Math.max(0, e.remaining - (now - e.startAt));
    }
  }
}

function resumeAll(): void {
  if (!paused) return;
  paused = false;
  for (const e of entries) {
    if (!e.dismissing) scheduleDismiss(e);
  }
}
